import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { finalize, interval, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { SettingsService } from 'src/app/services/services/settings.service';
import { realTimeService } from 'src/app/services/services/realtime.service';

type WindowRange = 'day' | 'week';

export interface TickerQueueRow {
  queue: string;

  totalOffered: number;
  totalAnswered: number;

  // survey
  avgSurveyScore?: number | null;
  formattedAvgSurveyScore?: string;

  // rates
  answerRate: number | null;              // 0..1 or null
  formattedAnswerRate: string;            // e.g. '83 %'

  answerRateWithinSla: number | null;     // 0..1 or null
  formattedAnswerRateWithinSla: string;   // e.g. '74 %'

  // times (seconds + formatted)
  secondsAvgWait: number | null;
  formattedSecondsAvgWait: string;

  secondsAvgSpeak: number | null;
  formattedSecondsAvgSpeak: string;

  secondsAvgWrapup: number | null;
  formattedSecondsAvgWrapup: string;

  // AHT (speak + wrapup)
  formattedSecondsAHT: string;
}

@Component({
    selector: 'app-ticker-queues',
    templateUrl: './ticker-queues.component.html',
    styleUrls: ['./ticker-queues.component.scss'],
    standalone: false
})
export class TickerQueuesComponent implements OnInit, OnDestroy {
  // Header + flags
  showSurveyScore = true;          // old: Model.ShowSurveyScore
  pollingIntervalMs = 10000;       // old: Model.AjaxRefreshInterval

  // Day/Week toggle
  selectedWindow: WindowRange = 'day';

  // Table
  displayedColumns: string[] = [];
  BreadcrumbsLink = [
    {"label": "Home", "url": "/"}, 
    {"label": "Real-Time", "url": "/real-time/overview"}, 
    {"label": "Ticker Queues", "url": "/real-time/ticker-queues"}];
  dataSource = new MatTableDataSource<TickerQueueRow>([]);
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  // UI state
  filter = '';
  isLoading = true;

  // polling
  private pollingSub?: Subscription;

  constructor(
    private dialog: MatDialog,
    private i18n: I18NextPipe,
    private realtimeService: realTimeService,
    private settingsService: SettingsService,
  ) {}

  async ngOnInit(): Promise<void> {
    var settings = await this.settingsService.realtimeSettings();
    this.pollingIntervalMs = Number(settings?.refreshInterval) * 1000 || 10000;
    this.showSurveyScore = settings?.showSurveyScore ?? false;
    this.displayedColumns = this.buildColumns();

    // numeric-aware sorting
    this.dataSource.sortingDataAccessor = (row: TickerQueueRow, column: string) => {
      switch (column) {
        case 'queue': return row.queue?.toLowerCase() ?? '';
        case 'totalOffered': return row.totalOffered ?? -1;
        case 'totalAnswered': return row.totalAnswered ?? -1;
        case 'avgSurveyScore': return row.avgSurveyScore ?? -1;
        case 'answerRate': return row.answerRate == null ? -1 : Math.round(row.answerRate * 100);
        case 'answerRateWithinSla': return row.answerRateWithinSla == null ? -1 : Math.round(row.answerRateWithinSla * 100);
        case 'secondsAvgWait': return row.secondsAvgWait ?? -1;
        case 'secondsAvgSpeak': return row.secondsAvgSpeak ?? -1;
        case 'secondsAvgWrapup': return row.secondsAvgWrapup ?? -1;
        case 'aht': {
          const speak = row.secondsAvgSpeak ?? 0;
          const wrap = row.secondsAvgWrapup ?? 0;
          return speak + wrap || -1;
        }
        default: return '';
      }
    };
    this.dataSource.sort = this.sort;

    // client-side filter
    this.dataSource.filterPredicate = (row, filterText) => {
      const f = (filterText || '').trim().toLowerCase();
      if (!f) return true;
      return [
        row.queue,
        row.formattedAvgSurveyScore,
        row.formattedAnswerRate,
        row.formattedAnswerRateWithinSla,
        row.formattedSecondsAvgWait,
        row.formattedSecondsAvgSpeak,
        row.formattedSecondsAvgWrapup,
        row.formattedSecondsAHT
      ].some(v => (v || '').toLowerCase().includes(f));
    };

    this.loadData(true);
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // --- Columns builder (survey column is conditional) ---
  private buildColumns(): string[] {
    const cols = [
      'queue',
      'totalOffered',
      'totalAnswered',
    ];
    if (this.showSurveyScore) cols.push('avgSurveyScore');
    cols.push(
      'answerRate',
      'answerRateWithinSla',
      'secondsAvgWait',
      'secondsAvgSpeak',
      'secondsAvgWrapup',
      'aht'
    );
    return cols;
  }

  // --- Data load (poll) ---
  private loadData(showLoader = false): void {
    if (showLoader) this.isLoading = true;


     this.realtimeService.getTickerQueues(this.selectedWindow === 'day' ? 0 : 1)
       .pipe(finalize(() => this.isLoading = false))
       .subscribe(data => this.dataSource.data = data?.rows ?? []);
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(this.pollingIntervalMs).subscribe(() => {
      this.loadData(false);
    });
  }
  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  // --- Filter handlers ---
  applyFilterFromProperty() {
    this.dataSource.filter = (this.filter || '').trim().toLowerCase();
  }
  clearFilter() {
    this.filter = '';
    this.dataSource.filter = '';
  }

  // --- Window toggle ---
  onSelectWindow(range: WindowRange) {
    if (this.selectedWindow === range) return;
    this.selectedWindow = range;
    this.loadData(true);
  }

  // --- Help ---
  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('ticker:help_title_queues'),
        text: this.i18n.transform('ticker:help_queues'),
        renderAsHtml: false
      }
    });
  }
}
